import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Button, Col, Modal, Row, Space, Tag, Tooltip } from "antd";
import moment from "moment";
import {
  CloseOutlined,
  DeleteOutlined,
  DownloadOutlined,
  FileTextOutlined,
  UploadOutlined,
} from "@ant-design/icons";
import { BiFilterAlt } from "react-icons/bi";
import { useForm } from "antd/lib/form/Form";
import { TABLE_ROWS_COUNT } from "../../shared/constants";
import { RootState } from "../../shared/types/common";
import DataTableComponent from "../../shared/components/data-table/data-table.component";
import GenericFormComponent from "../../shared/components/generic-form/generic-form.component";
import { ActiveFilterChipsBar } from "../../shared/components/active-filter-chips/active-filter-chips.component";
import { isFilterValueActive } from "../../shared/utils/active-filters";
import { accessAllowed, formatDateUtcToCurrent, showNotification } from "../../shared/utils/utils";
import { allPermissions } from "../../shared/constants/constants";
import {
  deleteCandidate,
  getCandidateList,
} from "../../core/store/reducers/candidate-screening.slice";
import UploadCandidatesComponent from "./components/upload-candidates.component";
import { Client } from "../../core/api-client";
import { API_GET_DOCUMENT_SIGNED_URL } from "../../shared/routes/private.routes";

const RESUME_FILTER_LABELS: Record<string, string> = {
  search: "Search",
  email: "Email",
  status: "Status",
  uploadedDateRange: "Uploaded Date",
};

function resumeFilterValueText(_key: string, value: any): string {
  if (_key === "uploadedDateRange" && Array.isArray(value)) {
    return value
      .map((date: any) => moment(date).format("DD-MMM-YYYY"))
      .join(" to ");
  }
  return Array.isArray(value) ? value.join(", ") : String(value);
}

const RESUME_FILTER_CLEAR: Record<string, any> = {
  search: undefined,
  email: undefined,
  status: [],
  uploadedDateRange: undefined,
};

const ResumeMasterPage: React.FC = () => {
  const dispatch = useDispatch();
  const [form] = useForm();
  const { loadingCandidates, candidateList, candidatePagination } = useSelector(
    (state: RootState) => state.candidateScreening
  );
  const { screenType } = useSelector((state: RootState) => state.settings);
  const { myPermissions } = useSelector((state: RootState) => state.auth);
  const canUploadResumes = accessAllowed(myPermissions, allPermissions.RESUME_UPLOAD);

  const [resumeListData, setResumeListData] = useState<any[]>([]);
  const [filterVisible, setFilterVisible] = useState(false);
  const [sorter, setSorter] = useState({ sort_by: "", sort_type: "DESC" });
  const [filters, setFilters] = useState<any>({});
  const [uploadOpen, setUploadOpen] = useState(false);
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [candidateToDelete, setCandidateToDelete] = useState<any>(null);

  const loadList = (
    page: any = null,
    pageSize: number = candidatePagination?.limit || TABLE_ROWS_COUNT
  ) => {
    const data = {
      page: page ? page : candidatePagination?.page ? candidatePagination.page : 1,
      limit: pageSize,
      ...sorter,
    };
    const filter: any = {};
    Object.keys(filters)?.forEach((val: any) => {
      if (filters[val]) filter[val] = filters[val];
    });
    if (filters.uploadedDateRange?.length === 2) {
      filter.uploadedStartDate = moment(filters.uploadedDateRange[0]).format(
        "YYYY-MM-DD"
      );
      filter.uploadedEndDate = moment(filters.uploadedDateRange[1]).format(
        "YYYY-MM-DD"
      );
      delete filter.uploadedDateRange;
    }
    dispatch(getCandidateList({ params: { ...data, ...filter } }));
  };

  useEffect(() => {
    loadList();
  }, [filters, sorter]);

  useEffect(() => {
    setResumeListData(
      (candidateList || []).map((obj: any, index: number) => ({
        key: index,
        ...obj,
      }))
    );
  }, [candidateList]);

  const activeFilterEntries = useMemo(() => {
    return Object.keys(filters)
      .filter((k) => isFilterValueActive(filters[k]))
      .map((key) => ({
        key,
        fieldLabel: RESUME_FILTER_LABELS[key] || key,
        valueText: resumeFilterValueText(key, filters[key]),
      }));
  }, [filters]);

  const clearAllFilters = useCallback(() => {
    setFilters({});
    form.resetFields();
  }, [form]);

  const removeFilter = useCallback(
    (key: string) => {
      setFilters((prev: any) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
      const cleared =
        key in RESUME_FILTER_CLEAR ? RESUME_FILTER_CLEAR[key] : undefined;
      form.setFieldsValue({ [key]: cleared });
    },
    [form]
  );

  const getSortOrder = (columnKey: string) => {
    if (sorter.sort_by !== columnKey) return null;
    return sorter.sort_type === "ASC" ? "ascend" : "descend";
  };

  const getSortableColumnProps = (columnKey: string) => ({
    sorter: true,
    sortOrder: getSortOrder(columnKey),
  });

  const onPageChange = (pageNumber: number) => loadList(pageNumber);
  const onPageSizeChange = (pageNumber: number, pageSize: number) => {
    loadList(pageNumber, pageSize);
  };

  const onChangeHandler = (_p: any, _f: any, tableSorter: any) => {
    const activeSorter = Array.isArray(tableSorter)
      ? tableSorter.find((item) => item?.order)
      : tableSorter;

    if (!activeSorter?.order) {
      setSorter({ sort_by: "", sort_type: "DESC" });
      return;
    }

    setSorter({
      sort_by: activeSorter.columnKey || "",
      sort_type: activeSorter.order === "ascend" ? "ASC" : "DESC",
    });
  };

  const handleDownload = async (record: any) => {
    if (!record?.resumeDocId) {
      showNotification("Resume file is not available for download.", "error");
      return;
    }
    try {
      const response = await Client.get(
        `${API_GET_DOCUMENT_SIGNED_URL}${record.resumeDocId}`
      );
      const downloadUrl = response?.data?.data?.url || response?.data?.url;
      if (!downloadUrl) {
        showNotification("Resume download URL is not available.", "error");
        return;
      }
      const link = document.createElement("a");
      link.href = downloadUrl;
      link.target = "_blank";
      link.rel = "noopener noreferrer";
      link.download =
        response?.data?.data?.fileName ||
        `${record?.fullName || "candidate"}-resume`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      showNotification("Failed to download resume.", "error");
    }
  };

  const handleDelete = () => {
    if (!candidateToDelete?.id) return;
    dispatch(deleteCandidate(candidateToDelete.id)).then((val: any) => {
      if (val?.payload?.data) {
        showNotification("Resume deleted successfully", "success");
        setDeleteModalVisible(false);
        setCandidateToDelete(null);
        loadList();
      }
    });
  };

  const formProps = {
    form: form,
    name: "resume-master-filters",
    enableEnterSubmit: true,
    formElements: [
      {
        name: "search",
        label: "",
        placeholder: "Search by name",
        type: "Input",
        colSpanResponsive: { xs: 12, sm: 8, md: 6, lg: 4 },
      },
      {
        name: "email",
        label: "",
        placeholder: "Email",
        type: "Input",
        colSpanResponsive: { xs: 12, sm: 8, md: 6, lg: 4 },
      },
      {
        name: "status",
        label: "",
        placeholder: "Status",
        type: "Select",
        colSpanResponsive: { xs: 12, sm: 8, md: 6, lg: 4 },
        showSearch: true,
        allowClear: true,
        mode: "multiple",
        options: [
          { value: "ACTIVE", label: "ACTIVE" },
          { value: "INACTIVE", label: "INACTIVE" },
        ],
      },
      {
        name: "uploadedDateRange",
        label: "",
        placeholder: ["Start date", "End date"],
        type: "RangePicker",
        colSpanResponsive: { xs: 24, sm: 12, md: 8, lg: 6 },
      },
    ],
    initialValues: {},
    extraButtons: [
      {
        htmlType: "submit",
        colSpanResponsive: { xs: 12, sm: 8, md: 6, lg: 3 },
        loading: false,
        submitButtonText: "Clear",
        type: "clearForm",
        onClick: clearAllFilters,
      },
      {
        htmlType: "submit",
        colSpanResponsive: { xs: 12, sm: 8, md: 6, lg: 3 },
        loading: false,
        submitButtonText: "Apply",
        submitButtonType: "primary",
      },
    ],
    onFinish: (values: any) => setFilters(values),
    onFinishFailed: null,
    onValuesChange: () => {
      return;
    },
  };

  const columns = [
    {
      title: "Name",
      dataIndex: "fullName",
      key: "fullName",
      ...getSortableColumnProps("fullName"),
      render: (name: string) => name || "-",
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
      ...getSortableColumnProps("email"),
      render: (email: string) => email || "-",
    },
    {
      title: "Phone",
      dataIndex: "phone",
      key: "phone",
      ...getSortableColumnProps("phone"),
      render: (phone: string) => phone || "-",
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      ...getSortableColumnProps("status"),
      render: (status: string) => (
        <Tag color={status === "ACTIVE" ? "green" : "default"}>
          {status || "-"}
        </Tag>
      ),
    },
    {
      title: "Uploaded Date",
      dataIndex: "createdDate",
      key: "createdDate",
      ...getSortableColumnProps("createdDate"),
      render: (date: string) => formatDateUtcToCurrent(date),
    },
    {
      title: "Actions",
      key: "actions",
      fixed: "right",
      render: (_text: any, record: any) => (
        <Space>
          <Tooltip title="Download">
            <Button
              type="link"
              icon={<DownloadOutlined />}
              onClick={() => handleDownload(record)}
              disabled={!record?.resumeDocId}
            />
          </Tooltip>
          <Tooltip title="Delete">
            <Button
              type="link"
              icon={<DeleteOutlined style={{ color: "#FF0000" }} />}
              onClick={() => {
                setCandidateToDelete(record);
                setDeleteModalVisible(true);
              }}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  const headerButtons = (
    <Space size="middle" className="app-page-actions">
      {canUploadResumes && (
        <Button icon={<UploadOutlined />} onClick={() => setUploadOpen(true)}>
          <span className="font-size-14">
            {!screenType?.isPortrait && "Upload Resumes"}
          </span>
        </Button>
      )}
      <Button
        shape="circle"
        type="primary"
        icon={<BiFilterAlt />}
        onClick={() => setFilterVisible(!filterVisible)}
        title={filterVisible ? "Hide filters" : "Show filters"}
      />
    </Space>
  );

  return (
    <div className="app-page">
      <div className="app-page-header">
        <Row align="middle" justify="space-between" wrap>
          <Col flex="1" style={{ minWidth: 0 }}>
            <div className="app-page-title-wrap">
              <div className="app-page-icon">
                <FileTextOutlined />
              </div>
              <h1 className="app-page-title">Resume Master</h1>
            </div>
            <p className="app-page-subtitle">
              Manage uploaded resumes and extracted candidate details
            </p>
          </Col>
          <Col>{headerButtons}</Col>
        </Row>
      </div>

      {filterVisible && (
        <div className="app-filter-bar">
          <div className="app-filter-header">
            <div className="app-filter-title">
              <BiFilterAlt style={{ fontSize: 14 }} />
              Filters
            </div>
            <Button
              type="text"
              size="small"
              icon={<CloseOutlined />}
              onClick={() => setFilterVisible(false)}
              title="Close filters"
              aria-label="Close filters"
            />
          </div>
          <div className="table-filter-form">
            <GenericFormComponent {...formProps} />
          </div>
        </div>
      )}

      <ActiveFilterChipsBar
        show={!filterVisible}
        entries={activeFilterEntries}
        onRemove={removeFilter}
        onClearAll={clearAllFilters}
      />

      <div className="app-table-card milestones-table-card">
        <DataTableComponent
          pagination={true}
          loading={loadingCandidates}
          dataSource={resumeListData}
          columns={columns}
          onChange={onChangeHandler}
          defaultPage={0}
          currentPage={candidatePagination.page}
          totalRows={candidatePagination?.total}
          onPageChange={onPageChange}
          pageSize={candidatePagination?.limit || TABLE_ROWS_COUNT}
          pageSizeOptions={["10", "15", "25", "50", "100"]}
          onPageSizeChange={onPageSizeChange}
        />
      </div>

      {uploadOpen && (
        <UploadCandidatesComponent
          onClose={() => setUploadOpen(false)}
          visible={uploadOpen}
          onSubmit={() => {
            setUploadOpen(false);
            loadList(1);
          }}
        />
      )}

      <Modal
        title="Delete Resume"
        open={deleteModalVisible}
        onOk={handleDelete}
        onCancel={() => {
          setDeleteModalVisible(false);
          setCandidateToDelete(null);
        }}
        okText="Delete"
        okButtonProps={{ danger: true }}
      >
        <p>
          Are you sure you want to delete{" "}
          {candidateToDelete?.fullName || "this resume"}?
        </p>
      </Modal>
    </div>
  );
};

export default ResumeMasterPage;
